import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export type ProjectOnboardingStatus =
  | 'draft' | 'submitted' | 'under_review' | 'sent_back' | 'clarification_required'
  | 'approved' | 'active' | 'closed' | 'rejected';

export type ProjectReviewDecision = 'approve' | 'reject' | 'send_back' | 'request_clarification';

export const PROJECT_TYPES = ['ev_charging_infrastructure', 'evcpd_deployment', 'charger_installation', 'solar_ev', 'om', 'amc', 'service', 'civil_work', 'electrical_work', 'survey', 'custom'] as const;
export const PROJECT_CATEGORIES = ['new_project', 'expansion', 'retrofit', 'maintenance', 'upgrade'] as const;
export const BUSINESS_UNITS = ['ev_infra', 'evcpd', 'service', 'amc', 'om'] as const;
export const BILLING_TYPES = ['site_wise', 'milestone_based', 'monthly', 'boq_based', 'lump_sum'] as const;
export const PROJECT_STATUSES = ['draft', 'active', 'on_hold', 'delayed', 'completed', 'closed', 'cancelled'] as const;
export const ZONES = ['north', 'south', 'east', 'west', 'central'] as const;
export const CHARGER_TYPES = ['ac_charger', 'dc_fast_charger', 'battery_swapping', 'evcpd'] as const;
export const SCOPE_OPTIONS = ['survey', 'electrical_installation', 'civil_foundation', 'canopy', 'earthing', 'signage', 'evcpd', 'commissioning', 'testing', 'om', 'amc', 'service'] as const;
export const PROJECT_DOC_TYPES = ['purchase_order', 'work_order', 'agreement', 'boq', 'sow', 'drawings', 'project_plan'] as const;
export const OPERATIONAL_STATUSES: Array<'active' | 'on_hold' | 'delayed' | 'completed' | 'closed' | 'cancelled'> = ['active', 'on_hold', 'delayed', 'completed', 'closed', 'cancelled'];

export interface ProjectDocument { id?: number; document_type: string; file_name?: string | null; file_url?: string | null; doc_status?: string }

export interface ProjectApplication {
  id: number;
  application_code: string;
  project_id: number | null;
  status: ProjectOnboardingStatus;
  current_stage: string;
  project_name: string;
  client_id: number | null;
  project_type: string | null;
  project_category: string | null;
  business_unit: string | null;
  project_value: number | null;
  currency: string;
  margin_pct: number | null;
  expected_profit_pct: number | null;
  billing_type: string | null;
  payment_terms: string | null;
  retention_applicable: boolean;
  retention_pct: number | null;
  start_date: string | null;
  planned_end_date: string | null;
  contract_duration_days: number | null;
  client_committed_date: string | null;
  gigforce_committed_date: string | null;
  project_status: string;
  client_pm_name: string | null;
  client_pm_mobile: string | null;
  client_pm_email: string | null;
  project_manager_id: number | null;
  regional_ops_manager_id: number | null;
  procurement_spoc_id: number | null;
  finance_spoc_id: number | null;
  installation_manager_id: number | null;
  project_manager_ids: number[];
  regional_ops_manager_ids: number[];
  procurement_spoc_ids: number[];
  finance_spoc_ids: number[];
  installation_manager_ids: number[];
  region_id: number | null;
  state_id: number | null;
  city: string | null;
  region_ids: number[];
  state_ids: number[];
  cities: string[];
  zone: string | null;
  zones: string[];
  oem: string | null;
  cpo: string | null;
  charger_brand: string | null;
  charger_type: string | null;
  scope_items: string[];
  source_type: string | null;
  submitted_at: string | null;
  created_at: string | null;
  completed_steps: number[];
  documents: ProjectDocument[];
  client_name?: string | null;
  client_code?: string | null;
  review_status?: string | null;
  internal_remarks?: string | null;
  workflow?: Array<{ id: number; stage: string; stage_order: number; status: string; assigned_role: string | null; remarks: string | null }>;
}

export const projectOnboardingApi = {
  async get(id: number): Promise<ProjectApplication> {
    return (await apiClient.get<ApiSuccess<ProjectApplication>>(`/project-onboarding/${id}`)).data.data;
  },
  async create(payload: Record<string, unknown>): Promise<ProjectApplication> {
    return (await apiClient.post<ApiSuccess<ProjectApplication>>('/project-onboarding', payload)).data.data;
  },
  async saveDraft(id: number, payload: Record<string, unknown>): Promise<ProjectApplication> {
    return (await apiClient.put<ApiSuccess<ProjectApplication>>(`/project-onboarding/${id}/draft`, payload)).data.data;
  },
  async submit(id: number): Promise<ProjectApplication> {
    return (await apiClient.post<ApiSuccess<ProjectApplication>>(`/project-onboarding/${id}/submit`, {})).data.data;
  },
  async respondClarification(id: number): Promise<ProjectApplication> {
    return (await apiClient.post<ApiSuccess<ProjectApplication>>(`/project-onboarding/${id}/respond-clarification`, {})).data.data;
  },
  async list(params: Record<string, unknown> = {}): Promise<{ data: ProjectApplication[]; meta: PaginationMeta }> {
    const { data } = await apiClient.get<ApiSuccess<ProjectApplication[]>>('/project-onboarding', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  async review(id: number, decision: ProjectReviewDecision, remarks?: string): Promise<ProjectApplication> {
    return (await apiClient.post<ApiSuccess<ProjectApplication>>(`/project-onboarding/${id}/review`, { decision, remarks })).data.data;
  },
  async withdraw(id: number): Promise<ProjectApplication> {
    return (await apiClient.post<ApiSuccess<ProjectApplication>>(`/project-onboarding/${id}/withdraw`, {})).data.data;
  },
  async remove(id: number): Promise<{ deleted: true }> {
    return (await apiClient.delete<ApiSuccess<{ deleted: true }>>(`/project-onboarding/${id}`)).data.data;
  },
  async clone(id: number): Promise<ProjectApplication> {
    return (await apiClient.post<ApiSuccess<ProjectApplication>>(`/project-onboarding/${id}/clone`, {})).data.data;
  },
  async reopen(id: number): Promise<ProjectApplication> {
    return (await apiClient.post<ApiSuccess<ProjectApplication>>(`/project-onboarding/${id}/reopen`, {})).data.data;
  },
  async changeStatus(id: number, status: string): Promise<ProjectApplication> {
    return (await apiClient.post<ApiSuccess<ProjectApplication>>(`/project-onboarding/${id}/status`, { status })).data.data;
  },
};

export interface ProjectOnboardingDashboard {
  kpis: Record<string, number>;
  charts: Record<string, Array<{ label: string; value: number }>>;
  tables: { recentlySubmitted: Array<Record<string, unknown>>; pendingApprovals: Array<Record<string, unknown>> };
}

export const projectDashboardApi = {
  async dashboard(): Promise<ProjectOnboardingDashboard> {
    return (await apiClient.get<ApiSuccess<ProjectOnboardingDashboard>>('/project-onboarding-dashboard')).data.data;
  },
};
